import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import RoleProtectedRoute from './RoleProtectedRoute';
import { AuthProvider } from './components/general/AuthContext';
import AppShell from './components/general/AppShell';
import Staff from './components/staff/Staff';
import AreaCoordinators from './components/staff/AreaCoordinators';
import EmployeeDetails from './components/staff/EmployeeDetails';
import BargainingUnits from './components/units/BargainingUnits';
import BargainingUnitInfo from './components/units/BargainingUnitInfo';
import Towns from './components/towns/Towns';
import Town from './components/towns/Town';
import UnassignedUnits from './components/units/ManageLocals';
import ScrollToTop from './components/general/ScrollToTop';
import Metrics from './components/metrics/Metrics';
import AuditLog from './components/audit/AuditLog';
import MembershipAuditsPage from './components/audit/MembershipAuditsPage';
import UsersAdmin from './components/admin/UsersAdmin';
import ReportsAdmin from './components/admin/ReportsAdmin';
import ReportsView from './components/reports/ReportsView';
import AssignmentsAdmin from './components/admin/AssignmentsAdmin';
import LoginWrapper from './components/general/LoginWrapper';
import VerifyLogin from './components/general/VerifyLogin';
import UnitManagement from './components/units/UnitManagement';
import OfficerChanges from './components/officers/OfficerChanges';
import { initGA, logPageView } from './analytics';
import logger from './utils/logger';
import './utils/migrateStorage';

const App = () => {
  const location = useLocation();

  useEffect(() => {
    logger.log('App', 'App component mounted');
    initGA();
    logPageView();
  }, []);

  useEffect(() => {
    logger.log('App', 'Route changed', { pathname: location.pathname });
    logPageView();
  }, [location]);

  return (
    <AuthProvider>
      <ScrollToTop />
      <Routes>
        {/* Public routes — no shell */}
        <Route path="/login" element={<LoginWrapper />} />
        <Route path="/login/verify" element={<VerifyLogin />} />

        {/* Protected routes — all wrapped in AppShell via layout route */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Metrics />} />
          <Route path="/metrics" element={<Metrics />} />
          <Route path="/units" element={<BargainingUnits />} />
          <Route path="/units/:id" element={<BargainingUnitInfo />} />
          <Route path="/unit-info/:id" element={<BargainingUnitInfo />} />
          <Route path="/towns" element={<Towns />} />
          <Route path="/town/:town" element={<Town />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/area-coordinators" element={<AreaCoordinators />} />
          <Route path="/employee/:id" element={<EmployeeDetails />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/membership-audits" element={<MembershipAuditsPage />} />
          <Route path="/reports" element={<ReportsView />} />
        </Route>

        {/* Admin/Manager routes */}
        <Route
          element={
            <RoleProtectedRoute allowedRoles={['Admin', 'Manager', 'admin', 'manager']}>
              <AppShell />
            </RoleProtectedRoute>
          }
        >
          <Route path="/unit-management" element={<UnitManagement />} />
          <Route path="/unassigned-units" element={<UnassignedUnits />} />
          <Route path="/officer-changes" element={<OfficerChanges />} />
        </Route>

        {/* Admin-only routes */}
        <Route
          element={
            <RoleProtectedRoute allowedRoles={['Admin', 'admin']}>
              <AppShell />
            </RoleProtectedRoute>
          }
        >
          <Route path="/admin/users" element={<UsersAdmin />} />
          <Route path="/admin/reports" element={<ReportsAdmin />} />
          <Route path="/admin/assignments" element={<AssignmentsAdmin />} />
        </Route>

        <Route
          path="*"
          element={
            <div style={{ padding: '40px', fontFamily: 'system-ui' }}>
              <h1>404 — Page Not Found</h1>
              <p>{window.location.pathname}</p>
            </div>
          }
        />
      </Routes>
    </AuthProvider>
  );
};

export default App;
